import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { shuffle, reject } from 'lodash';
import withTeam from '../../lib/with-team';
import { fetchProfilePageData, fetchPageData } from '../../store/teams/squad/actions';
import {
  isLoading as isLoadingSquadSelector,
  getLoadingError as getSquadLoadingErrorSelector,
  hasLoaded as hasLoadedSquadSelector,
  getProfile,
  getProfileSeasons,
  getProfileStats,
  getProfileFixtures,
  isLoadingProfile,
  getProfileLoadingError,
  getTeamCoaches,
  getTeamPlayers,
  hasLoadedProfile,
} from '../../store/teams/squad/selectors';
import { getActiveSeasonId } from '../../store/clubs/selectors';
import Profile from '../../components/team/profile';
import SeoHead from '../../components/seo';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import NoBackground from '../../components/club-frame/header/no-background';
import PROFILES_PROP_TYPES from '../../lib/prop-types/profile';
import { SEASONS_PROP_TYPES } from '../../lib/prop-types/season';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import TEAM_PROP_TYPES from '../../lib/prop-types/team';
import { FIXTURES_PROP_TYPE } from '../../lib/prop-types/fixture';
import ClubRouter from '../../lib/routing';
import sanitizeParams from '../../lib/routing/sanitize-params';

class ProfilePage extends React.Component {
  static async getInitialProps({ reduxStore, club, isServer, query, team, req, res }) {
    const { profileType, _profile, profileId: queryProfileId, seasonId: querySeasonId } = query;
    const seasonId = querySeasonId
      ? parseInt(querySeasonId, 10)
      : getActiveSeasonId(reduxStore.getState());
    const profileId = `${profileType === 'player' ? 'p' : 'c'}${queryProfileId}`;
    const initialProps = {
      activeSubSection: 'squad',
      pageType: 'team.squad.player',
      profileId,
      profileType,
      seasonId,
      canonicalRouteParams: {
        teamId: team.id,
        profileType,
        _profile,
        profileId: queryProfileId,
      },
    };

    if (!isServer) {
      return initialProps;
    }
    const [{ error, accessLevel }] = await Promise.all([
      reduxStore.dispatch(fetchProfilePageData(club.id, team.id, profileId, seasonId, req)),
      reduxStore.dispatch(fetchPageData(club.id, team.id, req)),
    ]);

    if (error && error.errorStatusCode && res) {
      res.statusCode = error.errorStatusCode;
    }

    if (accessLevel && accessLevel === 'private') {
      res.doNotCache = true;
    }

    return initialProps;
  }

  constructor(props) {
    super(props);
    const { profileId, profileType, players, coaches } = props;
    this.state = {
      relatedProfiles: this.selectRelatedProfiles(profileId, profileType, players, coaches),
    };
  }

  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate(previousProps) {
    this.loadIfRequired();
    const {
      team: { id: teamId },
      profileId,
      hasLoadedSquad,
    } = this.props;
    const {
      team: { id: previousTeamId },
      profileId: previousProfileId,
      hasLoadedSquad: previousHasLoadedSquad,
    } = previousProps;
    if (
      profileId !== previousProfileId ||
      teamId !== previousTeamId ||
      (hasLoadedSquad && !previousHasLoadedSquad)
    ) {
      this.updateRelatedProfiles();
    }
  }

  loadIfRequired = () => {
    const { isLoading, hasLoaded, loadingError } = this.props;
    if (!isLoading && !hasLoaded && !loadingError) {
      const {
        club: { id: clubId },
        loadData,
        team: { id: teamId },
        profileId,
        seasonId,
      } = this.props;
      loadData(clubId, teamId, profileId, seasonId);
    }

    const { isLoadingSquad, hasLoadedSquad, squadLoadingError } = this.props;
    if (!isLoadingSquad && !hasLoadedSquad && !squadLoadingError) {
      const {
        club: { id: clubId },
        loadSquad,
        team: { id: teamId },
      } = this.props;
      loadSquad(clubId, teamId);
    }
  };

  updateRelatedProfiles = () => {
    const { profileId, profileType, players, coaches } = this.props;
    this.setState({
      relatedProfiles: this.selectRelatedProfiles(profileId, profileType, players, coaches),
    });
  };

  selectRelatedProfiles = (profileId, profileType, players, coaches) => {
    const profiles = profileType === 'player' ? players : coaches;
    const currentProfileId = parseInt(profileId.substr(1), 10);
    return shuffle(reject(profiles, { id: currentProfileId })).slice(0, 6);
  };

  handleSeasonChange = (newSeasonId) => {
    const {
      club,
      profile,
      profileType,
      team: { id: teamId },
    } = this.props;
    ClubRouter.pushClubRoute(
      club,
      'team.squad.profile',
      sanitizeParams({
        teamId,
        profileType,
        _profile: profile.name,
        profileId: profile.id,
        seasonId: newSeasonId,
      }),
    );
  };

  render() {
    const {
      canonicalRouteParams,
      club,
      fixtures,
      isLoading,
      isLoadingSquad,
      loadingError,
      profile,
      profileType,
      seasonId,
      seasons,
      stats,
      team,
    } = this.props;
    const { id: clubId } = club;
    const { relatedProfiles } = this.state;

    if (loadingError) {
      return (
        <PageDataLoadError
          clubId={clubId}
          {...loadingError}
          accessDeniedTranslationKey="teams:profiles_access_denied"
        />
      );
    }

    return (
      <>
        <SeoHead
          descriptionTranslationKey="teams:profile_meta_description"
          route="team.squad.profile"
          routeParams={canonicalRouteParams}
          titleTranslationKey="teams:profile_page_title"
          translationValues={{
            profileName: profile && profile.name,
            profileType: canonicalRouteParams.profileType,
            teamName: team.name,
          }}
        />
        <NoBackground />
        <Profile
          club={club}
          onChangeSeason={this.handleSeasonChange}
          isLoading={isLoading}
          isLoadingSquad={isLoadingSquad}
          fixtures={fixtures}
          profile={profile}
          profileType={profileType}
          relatedProfiles={relatedProfiles}
          seasonId={seasonId}
          seasons={seasons}
          stats={stats}
          teamId={team.id}
        />
      </>
    );
  }
}

ProfilePage.defaultProps = {
  fixtures: [],
  loadingError: null,
  profile: null,
  seasonId: null,
  squadLoadingError: null,
  stats: null,
};

ProfilePage.propTypes = {
  canonicalRouteParams: PropTypes.shape().isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  coaches: PROFILES_PROP_TYPES.isRequired,
  fixtures: FIXTURES_PROP_TYPE,
  hasLoaded: PropTypes.bool.isRequired,
  hasLoadedSquad: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingSquad: PropTypes.bool.isRequired,
  loadData: PropTypes.func.isRequired,
  loadSquad: PropTypes.func.isRequired,
  loadingError: PropTypes.shape(),
  profile: PropTypes.shape(),
  profileId: PropTypes.string.isRequired,
  profileType: PropTypes.oneOf(['player', 'coach']).isRequired,
  seasons: SEASONS_PROP_TYPES.isRequired,
  seasonId: PropTypes.number,
  squadLoadingError: PropTypes.shape(),
  stats: PropTypes.shape(),
  players: PROFILES_PROP_TYPES.isRequired,
  team: TEAM_PROP_TYPES.isRequired,
};

const mapStateToProps = (state, { profileId, seasonId, teamId }) => ({
  coaches: getTeamCoaches(state, teamId),
  fixtures: getProfileFixtures(state, teamId, profileId, seasonId),
  hasLoaded: hasLoadedProfile(state, teamId, profileId, seasonId),
  hasLoadedSquad: hasLoadedSquadSelector(state, teamId),
  isLoading: isLoadingProfile(state, teamId, profileId, seasonId),
  isLoadingSquad: isLoadingSquadSelector(state, teamId),
  loadingError: getProfileLoadingError(state, teamId, profileId, seasonId),
  players: getTeamPlayers(state, teamId),
  profile: getProfile(state, teamId, profileId),
  seasons: getProfileSeasons(state, teamId, profileId, seasonId),
  squadLoadingError: getSquadLoadingErrorSelector(state, teamId),
  stats: getProfileStats(state, teamId, profileId, seasonId),
});

export default withTeam(
  connect(mapStateToProps, {
    loadData: fetchProfilePageData,
    loadSquad: fetchPageData,
  })(ProfilePage),
);
